import AOS from "aos";
import "aos/dist/aos.css";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { HiArrowRight } from "react-icons/hi2";
import { Link } from "react-scroll";
import { FlipWords } from "../animations/flip-words";
import { HoverBorderGradient } from "../animations/hover-border-gradient";
import Beam from "../beam";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalTrigger,
  useModal,
} from "../ui/animated-modal";
import { Badge } from "../ui/badge";
import { Container } from "../ui/container";
import { Heading } from "../ui/heading";
import { Subheading } from "../ui/subheading";
import Countdown from "./Countdown";

export const Hero = ({ prices }) => {
  const { setOpen } = useModal();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const [isMobile, setIsMobile] = useState(false);
  const today = new Date();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const scaleDimensions = () => (isMobile ? [0.7, 0.9] : [1.05, 1.2]);
  const rotate = useTransform(scrollYProgress, [0, 0.5], [20, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], scaleDimensions());
  const translate = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const subTitles = [
    "Redefining Woocommerce Store Management",
    "Whizmanage is a novel plugin for managing all your Woocommerce store",
    "management needs from a single friendly dashboard. Designed for non-techies",
    "you can easily edit and manage products, inventory, orders, coupons, user-defined fields, customers and more.",
  ];

  const promotion = prices.find(
    (price) => price.on_sale && new Date(price.date_on_sale_to_gmt) > today
  );

  const buttonVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.4, ease: "easeInOut" },
    },
  };

  useEffect(() => {
    AOS.init({ duration: 1200 });
  }, []);

  const HTMLToText = (textHtml) => {
    const parser = new DOMParser();
    const htmlString = textHtml?.toString();
    const doc = parser.parseFromString(htmlString, "text/html");
    return doc.body.textContent;
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col pt-32 relative overflow-hidden"
    >
      <Container className="flex flex-col items-center justify-center dark">
        {promotion && (
          <Modal className="dark">
            <motion.div
              initial="hidden"
              animate={promotion ? "visible" : "hidden"}
              variants={buttonVariants}
            >
              <ModalTrigger>
                <Badge className="notranslate">
                  <span className="mr-1">
                    🎉 Limited-Time Offer, Don't Miss Out!
                  </span>
                  <Countdown
                    targetDate={promotion.date_on_sale_to_gmt}
                    shortVersion={true}
                  />
                </Badge>
              </ModalTrigger>
            </motion.div>
            <ModalBody>
              <ModalContent>
                <div className="flex flex-col justify-center items-center text-center space-y-4 text-neutral-300">
                  <h2 className="text-xl font-bold">
                  {prices[0].short_description}
                  </h2>
                  <p className="text-base" >{HTMLToText(prices[0].description)}</p>
                  <Countdown
                    targetDate={promotion.date_on_sale_to_gmt}
                    shortVersion={false}
                  />
                  <p className="text-base font-semibold">
                    Hurry, the offer ends soon!
                  </p>
                </div>
              </ModalContent>
              <ModalFooter className="gap-4 !justify-center" close={setOpen}>
                <Link
                  activeClass="active"
                  spy={true}
                  to="pricing"
                  smooth="easeInOutQuint"
                  duration={1000}
                  delay={200}
                  offset={20}
                  onClick={() => close(false)}
                >
                  <HoverBorderGradient
                    containerClassName="rounded-full"
                    as="button"
                    className="dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2 group"
                  >
                    <span>Purchase Now</span>
                    <HiArrowRight className="text-white group-hover:translate-x-1 stroke-[1px] h-3 w-3 mt-0.5 transition-transform duration-200" />
                  </HoverBorderGradient>
                </Link>
              </ModalFooter>
            </ModalBody>
          </Modal>
        )}
        <Heading
          as="h1"
          className="text-4xl lg:text-8xl font-semibold max-w-6xl mx-auto text-center relative z-10 py-6 hidden md:block"
          data-aos="fade-right"
        >
          Empower Your Store, Simplify Your Success
        </Heading>
        <Heading
          as="h1"
          className="text-4xl lg:text-8xl font-semibold max-w-6xl mx-auto text-center relative z-10 py-6 md:hidden"
        >
          Empower Your Store, Simplify Your Success
        </Heading>
        <div className="hidden md:block" data-aos="fade-left">
          <Subheading className="min-h-16 text-center mt-2 md:mt-6 text-base md:text-xl text-muted dark:text-muted-dark w-3xl mx-auto relative z-10 dark notranslate">
            <FlipWords words={subTitles} />
          </Subheading>
        </div>
        <div className="md:hidden">
          <Subheading className="min-h-16 text-center mt-2 md:mt-6 text-base md:text-xl text-muted dark:text-muted-dark w-3xl mx-auto relative z-10 dark notranslate">
            <FlipWords words={subTitles} />
          </Subheading>
        </div>
        <div
          className="md:flex items-center gap-4 justify-center my-10 relative z-10 dark hidden"
          data-aos="fade-up"
        >
          <Link
            activeClass="active"
            spy={true}
            to="pricing"
            smooth="easeInOutQuint"
            duration={1000}
            delay={200}
            offset={20}
          >
            <HoverBorderGradient
              containerClassName="rounded-full"
              as="button"
              className="dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2 group"
            >
              <span>Get started</span>
              <HiArrowRight className="text-white group-hover:translate-x-1 stroke-[1px] h-3 w-3 mt-0.5 transition-transform duration-200" />
            </HoverBorderGradient>
          </Link>
        </div>
        <div
          className="flex items-center gap-4 justify-center my-10 relative z-10 dark md:hidden"
        >
          <Link
            activeClass="active"
            spy={true}
            to="pricing"
            smooth="easeInOutQuint"
            duration={1000}
            delay={200}
            offset={20}
          >
            <HoverBorderGradient
              containerClassName="rounded-full"
              as="button"
              className="dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2 group"
            >
              <span>Get started</span>
              <HiArrowRight className="text-white group-hover:translate-x-1 stroke-[1px] h-3 w-3 mt-0.5 transition-transform duration-200" />
            </HoverBorderGradient>
          </Link>
        </div>
      </Container>
      <div
        className="flex items-center justify-center relative p-0 cursor-pointer !w-full md:hidden"
      >
        <div
          className="w-full relative"
          style={{
            perspective: "1000px",
          }}
        >
          <Card rotate={rotate} translate={translate} scale={scale}></Card>
        </div>
      </div>
      <div
        className="hidden md:flex items-center justify-center relative p-28 cursor-pointer"
        data-aos="fade-up"
        data-aos-anchor-placement="top-bottom"
        data-aos-easing="ease-out-cubic"
      >
        <div
          className="w-full relative"
          style={{
            perspective: "1000px",
          }}
        >
          <Card rotate={rotate} translate={translate} scale={scale}></Card>
        </div>
      </div>
    </div>
  );
};

export const Card = ({ rotate, scale, translate, children }) => {
  return (
    <motion.div
      style={{
        rotateX: rotate,
        translateY: translate,
        scale: scale,
        boxShadow:
          "0 0 #0000004d, 0 9px 20px #0000004a, 0 37px 37px #00000042, 0 84px 50px #00000026, 0 149px 60px #0000000a, 0 233px 65px #00000003",
      }}
      className="md:max-w-6xl mb-4 z-40 group mx-auto isolate group w-full border-4 border-neutral-900 p-2 bg-black rounded-[30px] shadow-2xl shadow-brand/[0.8] relative"
    >
      <Beam showBeam className="-top-1 block" />
      <div className="absolute max-h-fit inset-0 z-20  bg-transparent group-hover:bg-black/0 transition-all duration-200 flex items-center justify-center"></div>
      <div className="max-h-fit w-full overflow-hidden rounded-2xl bg-transparent md:rounded-2xl md:p-2">
        <video
        className="rounded"
          src="https://whizmanage.com/wp-content/uploads/2024/10/Whizmanage-App-Demo-Overview.mp4.mp4"
          loop
          autoPlay
          muted
        />
        {children}
      </div>
    </motion.div>
  );
};
